/**
 * Taxonomy Filter Component
 *
 * Reusable disclosure-pattern dropdown filter that navigates via URL query
 * parameters. Uses the same aria-expanded toggle pattern as the TOC block.
 * Works with any `.taxonomy-filter` element.
 *
 * @package Commcore26
 */

export const TaxonomyFilter = {
	init() {
		const filters = document.querySelectorAll('.taxonomy-filter');

		if (!filters.length) {
			return;
		}

		filters.forEach((filter) => this.initFilter(filter));

		// Initialize search inputs.
		document.querySelectorAll('.taxonomy-filter__search').forEach((wrapper) => {
			const input = wrapper.querySelector('.taxonomy-filter__search-input');
			const submit = wrapper.querySelector('.taxonomy-filter__search-submit');

			if (!input) {
				return;
			}

			const performSearch = () => {
				const param = input.dataset.param;

				if (!param) {
					return;
				}

				const url = new URL(window.location.href);
				url.searchParams.delete('paged');

				if (input.value.trim()) {
					url.searchParams.set(param, input.value.trim());
				} else {
					url.searchParams.delete(param);
				}

				window.location.href = url.toString();
			};

			input.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					performSearch();
				}
			});

			if (submit) {
				submit.addEventListener('click', (e) => {
					e.preventDefault();
					performSearch();
				});
			}
		});

		// Close any open dropdown when clicking outside.
		document.addEventListener('click', (e) => {
			if (!e.target.closest('.taxonomy-filter')) {
				this.closeAll();
			}
		});
	},

	initFilter(filter) {
		const dropdowns = filter.querySelectorAll('.taxonomy-filter__dropdown');

		dropdowns.forEach((dropdown) => this.initDropdown(dropdown, filter));
	},

	initDropdown(dropdown, filter) {
		const toggle = dropdown.querySelector('.taxonomy-filter__toggle');
		const list = dropdown.querySelector('.taxonomy-filter__list');

		if (!toggle || !list) {
			return;
		}

		// Toggle dropdown on button click.
		toggle.addEventListener('click', () => {
			const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

			// Close other open filters first.
			this.closeAll();

			toggle.setAttribute('aria-expanded', !isExpanded);
		});

		// Handle option clicks.
		// data-param can be on the dropdown itself or the parent filter wrapper.
		const param = dropdown.dataset.param || filter.dataset.param;

		list.querySelectorAll('.taxonomy-filter__option').forEach((option) => {
			option.addEventListener('click', (e) => {
				e.preventDefault();
				const value = option.dataset.value;

				if (!param) {
					return;
				}

				const url = new URL(window.location.href);
				url.searchParams.delete('paged');

				if (value) {
					url.searchParams.set(param, value);
				} else {
					url.searchParams.delete(param);
				}

				window.location.href = url.toString();
			});
		});

		// Keyboard navigation.
		toggle.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				toggle.setAttribute('aria-expanded', 'false');
				toggle.focus();
			}
		});

		list.addEventListener('keydown', (e) => {
			const options = [...list.querySelectorAll('.taxonomy-filter__option')];
			const current = document.activeElement;
			const index = options.indexOf(current);

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					if (index < options.length - 1) {
						options[index + 1].focus();
					}
					break;
				case 'ArrowUp':
					e.preventDefault();
					if (index > 0) {
						options[index - 1].focus();
					} else {
						toggle.focus();
					}
					break;
				case 'Escape':
					toggle.setAttribute('aria-expanded', 'false');
					toggle.focus();
					break;
				case 'Home':
					e.preventDefault();
					options[0]?.focus();
					break;
				case 'End':
					e.preventDefault();
					options[options.length - 1]?.focus();
					break;
			}
		});
	},

	closeAll() {
		document.querySelectorAll('.taxonomy-filter__toggle[aria-expanded="true"]').forEach((t) => {
			t.setAttribute('aria-expanded', 'false');
		});
	},
};
